import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2, Clock, MapPin, Monitor, AlertTriangle, User, Users } from "lucide-react";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";
import { fmtDate, fmtDateOnly } from "@/lib/time";
import api, { apiError } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmDialog";
import { TICKET_STATUS, statusMeta, categoryMeta, prioMeta, typeMeta, isOverdue, assigneeLabel } from "@/lib/tickets";

export default function TicketCard({ ticket, users = [], onEdit, onChanged, showLocation = false, locationName = "" }) {
  const { lang } = useLang();
  const de = lang === "de";
  const { user } = useAuth();
  const canEdit = can(user, "tickets", "edit");
  const canDelete = can(user, "tickets", "delete");
  const navigate = useNavigate();
  const confirm = useConfirm();
  const t = ticket;
  const overdue = isOverdue(t);
  const cat = categoryMeta(t.category);
  const pm = prioMeta(t.priority);

  const setStatus = async (status) => {
    try { await api.patch(`/tickets/${t.id}`, { status }); window.dispatchEvent(new Event("tickets:changed")); onChanged && onChanged(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const del = async () => {
    const ok = await confirm({ title: de ? "Ticket löschen?" : "Delete ticket?", message: t.title, confirmLabel: de ? "Löschen" : "Delete", danger: true });
    if (!ok) return;
    try { await api.delete(`/tickets/${t.id}`); window.dispatchEvent(new Event("tickets:changed")); onChanged && onChanged(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const closed = t.status === "closed";

  return (
    <div data-testid={`ticket-${t.id}`} className={`h-full flex flex-col rounded-lg p-3 border transition-colors hover:border-[#c00000] ${closed ? "bg-white/[0.02] border-gray-100" : overdue ? "bg-red-500/[0.06] border-red-500/30" : "bg-gray-50 border-gray-200"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className={`text-sm font-medium ${closed ? "text-gray-600 line-through" : "text-gray-900"}`}>{t.title}</div>
          {t.note && <div className="text-xs text-gray-600 mt-0.5 whitespace-pre-wrap">{t.note}</div>}
        </div>
        <span className={`shrink-0 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${pm.cls}`}>{de ? pm.de : pm.en}</span>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mt-2">
        <span data-testid={`ticket-type-badge-${t.id}`} className={`text-[11px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${typeMeta(t.ticket_type).cls}`}>{de ? typeMeta(t.ticket_type).de : typeMeta(t.ticket_type).en}</span>
        {showLocation && (
          <button data-testid={`ticket-loc-${t.id}`} onClick={() => navigate(`/locations/${encodeURIComponent(t.location_id)}`)} className="flex items-center gap-1 text-[11px] font-mono px-1.5 py-0.5 rounded border border-amber-500/30 bg-amber-500/10 text-amber-700 hover:bg-amber-500/20 transition-colors">
            <MapPin className="w-3 h-3" /> {t.location_id}{locationName ? ` · ${locationName}` : ""}
          </button>
        )}
        <span className="text-[11px] px-1.5 py-0.5 rounded border border-gray-200 bg-gray-50 text-gray-700">{de ? cat.de : cat.en}</span>
        {t.device_code && (
          <button data-testid={`ticket-device-${t.id}`} onClick={() => navigate(`/d/${encodeURIComponent(t.device_code)}`)} className="flex items-center gap-1 text-[11px] font-mono px-1.5 py-0.5 rounded border border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors">
            <Monitor className="w-3 h-3" /> {t.device_code}
          </button>
        )}
        {t.due_date && (
          <span className={`flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border ${overdue ? "border-red-500/40 bg-red-500/15 text-red-700" : "border-gray-200 bg-gray-50 text-gray-700"}`}>
            {overdue && <AlertTriangle className="w-3 h-3" />}<Clock className="w-3 h-3" /> {fmtDateOnly(t.due_date)}{overdue ? (de ? " · überfällig" : " · overdue") : ""}
          </span>
        )}
        {t.assignee && (
          <span className="flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border border-gray-200 bg-gray-50 text-gray-700">
            {t.assignee_type === "group" ? <Users className="w-3 h-3" /> : <User className="w-3 h-3" />} {assigneeLabel(t, users, de)}
          </span>
        )}
      </div>

      {((t.items && t.items.length > 0) || (t.shipping_status && t.shipping_status !== "offen") || t.tracking_number) && (
        <div className="flex flex-wrap items-center gap-1.5 mt-2">
          {(t.items || []).map((it, i) => (
            <span key={i} className="text-[11px] px-1.5 py-0.5 rounded border border-gray-200 bg-gray-50 text-gray-700"><span className="font-mono">{it.qty}×</span> {it.name}</span>
          ))}
          {t.shipping_status && t.shipping_status !== "offen" && (
            <span className="text-[11px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border border-sky-200 bg-sky-50 text-sky-700">{({ versendet: de ? "versendet" : "shipped", zugestellt: de ? "zugestellt" : "delivered", erhalten: de ? "erhalten" : "received" })[t.shipping_status] || t.shipping_status}</span>
          )}
          {t.tracking_number && <span className="text-[11px] font-mono px-1.5 py-0.5 rounded border border-gray-200 bg-gray-50 text-gray-700">📦 {t.tracking_number}</span>}
        </div>
      )}

      <div className="flex items-center justify-between gap-2 mt-auto pt-3">
        <div className="flex items-center gap-2">
          <select
            data-testid={`ticket-status-${t.id}`}
            value={t.status}
            disabled={!canEdit}
            onChange={(e) => setStatus(e.target.value)}
            className="text-xs font-medium rounded-lg border border-gray-300 bg-white text-gray-800 px-2 py-1.5 focus:outline-none focus:border-[#c00000] disabled:opacity-70"
          >
            {TICKET_STATUS.map((s) => <option key={s.id} value={s.id} className="bg-gray-50 text-gray-900">{de ? s.de : s.en}</option>)}
          </select>
          <span className="flex items-center gap-1 text-[11px] text-gray-600"><Clock className="w-3 h-3" /> {fmtDate(t.created_at)}</span>
        </div>
        <div className="flex items-center gap-1">
          {canEdit && <button data-testid={`ticket-edit-${t.id}`} onClick={() => onEdit && onEdit(t)} className="flex items-center gap-1 text-[11px] bg-gray-50 border border-gray-200 text-gray-600 rounded px-2 py-1 hover:text-gray-900 transition-colors"><Pencil className="w-3 h-3" /></button>}
          {canDelete && <button data-testid={`ticket-delete-${t.id}`} onClick={del} className="flex items-center gap-1 text-[11px] bg-gray-50 border border-gray-200 text-gray-600 rounded px-2 py-1 hover:text-red-700 hover:border-red-500/30 transition-colors"><Trash2 className="w-3 h-3" /></button>}
        </div>
      </div>
    </div>
  );
}
